import { BoardInfo, NavigateAction, RowBase } from './types';
import { hasFeatureTag } from '../../tools/featureToggle/utils';
import { FiltersValues, KpiType, SortingInfo, SortType } from '../../types';
import { getDateRangeLabel, parseDateAsValue } from '../../filters/dateRange';
import { ContextManager } from '../../context/manager';

export function insertUniqueOutOfStackRows(source: RowBase[], target: RowBase[], maxSize: number = 5) {
  const targetIds = new Set(target.map(row => row.id));
  for (const row of source) {
    if (!targetIds.has(row.id)) {
      target.push(row);
      targetIds.add(row.id);
    }
    if (target.length >= maxSize) break;
  }
}

export function getOutOfStackRows(
  baseRows: RowBase[],
  limitedRows: RowBase[],
  highlight: string,
  brandOwner: string,
  isFocusTypeBrandOwnerGroup: boolean,
  billboardDataType: string
) {
  const outOfStackRows: RowBase[] = [];

  // Exit early if highlight already exists in limitedRows
  if (limitedRows.some(row => row.highlight === highlight)) {
    return outOfStackRows;
  }

  //comparison is done based on whether the focus type is a brand owner group
  //if it is, we need to find all rows with the same brand owner and highlight it
  // Determine indexes of interest
  const filteredOutOfStackRows: RowBase[] = isFocusTypeBrandOwnerGroup
    ? baseRows.filter(row => row.brandOwner === highlight)
    : baseRows.filter(
        row => row.highlight === highlight || (billboardDataType !== 'brand_name' && row.brandOwner === brandOwner)
      );

  insertUniqueOutOfStackRows(filteredOutOfStackRows, outOfStackRows);

  return outOfStackRows;
}

/**
 * Determine if the billboard should be hidden based on its info.
 * This is used to hide the Top Product Launched billboard in MO
 * when there are no data.
 */
export function shouldHideBillboard(info: BoardInfo | undefined) {
  if (!info) return false;

  // Hide billboard if there are no rows and the title includes Top Product Launched title
  const topProductLaunchedTitle = 'Top products launched in';
  if (info.rows.length === 0 && info.title.includes(topProductLaunchedTitle) && hasFeatureTag('m2-reduced-content')) {
    return true;
  }

  return false;
}

export const isMoBillboardDataTypeBrandName = (moBillboardDataType: string[] | undefined) => {
  return moBillboardDataType?.[0] === 'brand_name';
};

export const syncSettingsOnBillboardKpiSelection = (
  moBillboardDataType: string[],
  kpis: string[],
  actionConfig: NavigateAction
) => {
  const settings = actionConfig.action.payload.settings;

  settings.page.sortingInfo = syncSortingInfo(kpis, settings.page.sortingInfo);
  syncFiltersOnBillboardNavigation(actionConfig.filtersToUpdate);

  if (isMoBillboardDataTypeBrandName(moBillboardDataType)) {
    settings.components.leaderboard.lbColumns = kpis;
  } else {
    settings.components.productLeaders.plColumns = kpis;
  }
};

export function syncSortingInfo(kpis: string[], sortingInfo: string[]) {
  const sortingInfoJson: SortingInfo | undefined = sortingInfo?.[0]
    ? (JSON.parse(sortingInfo?.[0]) as SortingInfo)
    : undefined;

  if (sortingInfoJson) {
    const { sortType, direction, sortIndex, label } = sortingInfoJson;

    const kpiChangeKey = sortType === SortType.Change ? `change_${kpis[0]}` : kpis[0];

    sortingInfoJson.columnName = sortType === SortType.Change ? kpis[0].concat(`|${kpiChangeKey}`) : kpis[0];
    sortingInfoJson.sortKey = sortType === SortType.Change ? `change_${kpis[0]}` : kpis[0];
    sortingInfoJson.sortType = sortType;
    sortingInfoJson.label = label;
    sortingInfoJson.sortIndex = sortIndex;
    sortingInfoJson.direction = direction;
  }

  return [JSON.stringify(sortingInfoJson)];
}

export const syncFiltersOnBillboardNavigation = (filtersToUpdate: Record<string, string[]>) => {
  if (!filtersToUpdate) {
    return;
  }

  const firstActivityStartDate = new Date(filtersToUpdate[KpiType.FirstActivity][0]);
  const firstActivityEndDate = new Date(filtersToUpdate[KpiType.FirstActivity][1]);

  const startValue = parseDateAsValue(firstActivityStartDate);
  const endValue = parseDateAsValue(firstActivityEndDate);

  const firstActivityValue: FiltersValues = {
    [KpiType.FirstActivity]: [
      {
        value: `${startValue.value}-${endValue.value}`,
        label: getDateRangeLabel(startValue.label, endValue.label),
      },
    ],
  };

  ContextManager.getInstance().updateFilterValues(firstActivityValue);
};
